import { useState, useEffect } from 'react';
import { useApi } from '../../../utils/hooks/useApi';
import { IMembershipReportDto } from '../../../utils/constants/interfaces';

interface UseMembershipReportReturn {
  membershipData: IMembershipReportDto | null;
  isLoading: boolean;
  error: string | null;
  fetchReport: () => Promise<void>;
  reportDate: string;
}

export const useMembershipReport = (): UseMembershipReportReturn => {
  const [membershipData, setMembershipData] = useState<IMembershipReportDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportDate, setReportDate] = useState<string>('');

  const api = useApi();

  const fetchReport = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const endpoint = '/members/reports/membership-report';

      const apiResponse = await api.get<IMembershipReportDto>(endpoint);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        setMembershipData(apiResponse.response);

        // Set report date based on day of month
        // The report may be generated at the end of the month for the next monthly meeting.
        // Round up to next month if day > 15
        const currentDate = new Date();
        const day = currentDate.getDate();
        const reportDate = new Date(currentDate);

        if (day > 15) {
          // Second half of month - show next month
          reportDate.setMonth(reportDate.getMonth() + 1);
        }

        const monthNames = [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ];
        const reportDateString = `${monthNames[reportDate.getMonth()]} ${reportDate.getFullYear()}`;
        setReportDate(reportDateString);
      } else {
        const errorMessage =
          apiResponse?.status?.statusDescription || 'Failed to fetch membership report data';
        setError(errorMessage);
        console.error('Error fetching membership report:', errorMessage);
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred while fetching the membership report';
      setError(errorMessage);
      console.error('Error fetching membership report:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Auto-fetch report when component mounts
    void fetchReport();
  }, []);

  return {
    membershipData,
    isLoading,
    error,
    fetchReport,
    reportDate
  };
};
